import { isSameMember } from './membroFamiliar';

/*
 * Regras dos documentos da inscrição pública, extraídas de FormDocumentos.jsx
 * e FormSocioEconomicos.jsx para funções puras testáveis sem montar
 * componente.
 */

/*
 * Um anexo é aprovado quando o back manda `aprovado` (contrato novo,
 * calculado a partir da avaliação ativa dele, tolerando lista nula). Sem esse
 * campo — resposta antiga, ou anexo novo que ainda não foi salvo — cai na
 * regra local de sempre: alguma avaliação com status 'A', 'APROVADO' ou '1'.
 */
export const isAnexoAprovado = (anexo) => {
    if (typeof anexo?.aprovado === 'boolean') {
        return anexo.aprovado;
    }

    const avaliacoes = anexo?.inscricaoAnexoAvaliacaoList || [];
    return avaliacoes.some(avaliacao => avaliacao.status === 'A' || avaliacao.status === 'APROVADO' || avaliacao.status === '1');
};

/*
 * Pendências que nenhum anexo atual resolve. O vínculo é
 * `anexo.idInscricaoPendenciaAnexo === pendencia.id`, guardado no anexo desde
 * o envio; sem alterar `inscricaoPendenciaAnexos` do jeito que veio do back,
 * remover o envio (ou nunca ter enviado) já basta para a pendência voltar a
 * aparecer aqui.
 */
export const pendenciasEmAberto = (pendencias = [], anexos = []) => (pendencias || []).filter(
    pendencia => !(anexos || []).some(anexo => anexo.idInscricaoPendenciaAnexo === pendencia.id)
);

/*
 * Envio novo (sem id) sempre pode ser removido — ainda não foi para o back.
 * Documento já salvo só sai com a entrega de documentos aberta e se não
 * estiver aprovado (aprovado só a instituição remove).
 */
export const podeRemoverAnexo = (anexo, { permitirEntregaDocumentos = false } = {}) => {
    if (!anexo?.id) {
        return true;
    }

    return permitirEntregaDocumentos && !isAnexoAprovado(anexo);
};

/*
 * O que prende um integrante ao grupo familiar: as pendências dele e os
 * anexos dele, já separados entre salvos (têm id, o back sabe deles) e novos
 * (só existem no formulário ainda). Quem chama decide o que fazer com cada
 * grupo — ex.: bloquear a remoção do integrante, ou remover os anexos novos
 * junto com ele.
 */
export const vinculosDoMembro = (membro, { anexos = [], pendencias = [] } = {}) => {
    const pendenciasDoMembro = (pendencias || []).filter(
        pendencia => pendencia.composicaoFamiliar && isSameMember(pendencia.composicaoFamiliar, membro)
    );
    const anexosDoMembro = (anexos || []).filter(
        anexo => anexo.composicaoFamiliar && isSameMember(anexo.composicaoFamiliar, membro)
    );

    return {
        pendencias: pendenciasDoMembro,
        anexosSalvos: anexosDoMembro.filter(anexo => anexo.id),
        anexosNovos: anexosDoMembro.filter(anexo => !anexo.id)
    };
};

/*
 * Margem para o limite padrão de 10 MB do corpo da requisição (tudo em JSON,
 * com o base64 dos arquivos novos): 8 MB de arquivos novos deixa espaço para
 * o resto do payload.
 */
export const LIMITE_ENVIO_BASE64 = 8 * 1024 * 1024;

/*
 * Soma do tamanho (em caracteres da string base64, não em bytes do arquivo
 * original) dos anexos novos — só eles viajam com o arquivo no corpo da
 * requisição, porque o anexo salvo é imutável — mais o comprovante do ENEM,
 * quando ele também está para enviar (string; o que já está salvo não volta
 * como base64).
 */
export const tamanhoEnvioBase64 = (anexos = [], inscricaoEnem) => {
    const totalAnexosNovos = (anexos || [])
        .filter(item => !item.id)
        .reduce((soma, item) => soma + (typeof item.anexo?.arquivo === 'string' ? item.anexo.arquivo.length : 0), 0);

    const tamanhoEnem = typeof inscricaoEnem?.arquivo === 'string' ? inscricaoEnem.arquivo.length : 0;

    return totalAnexosNovos + tamanhoEnem;
};

/*
 * Descrição cortada em `max` caracteres (o back guarda em VARCHAR2(40)):
 * usada tanto ao salvar quanto ao reabrir uma pendência cuja descrição já
 * veio maior do que isso.
 */
export const truncarDescricao = (texto, max = 40) => {
    if (texto === null || texto === undefined) {
        return '';
    }

    return String(texto).slice(0, max);
};
